import { api } from './api';
import type { 
  Checklist, 
  ChecklistListItem, 
  ChecklistRun, 
  ChecklistRunListItem, 
  ChecklistStepRun, 
  CreateChecklistRunRequest, 
  UpdateChecklistStepRunRequest, 
  SkipStepRequest 
} from '../types/checklists';

interface PaginatedResponse<T> {
  count: number;
  results: T[];
  next?: string | null;
  previous?: string | null;
}

export const ChecklistsAPI = {
  // Checklist management
  async list(page?: number): Promise<PaginatedResponse<ChecklistListItem>> {
    const url = page && page > 1
      ? `/api/checklists/checklists/?page=${page}`
      : '/api/checklists/checklists/';
    const res = await api.get<ChecklistListItem[] | PaginatedResponse<ChecklistListItem>>(url);
    if (Array.isArray(res.data)) {
      return { count: res.data.length, results: res.data, next: null, previous: null };
    } else if (res.data && 'results' in res.data) {
      return res.data as PaginatedResponse<ChecklistListItem>;
    }
    return { count: 0, results: [], next: null, previous: null };
  },

  async retrieve(id: number): Promise<Checklist> {
    const res = await api.get<Checklist>(`/api/checklists/checklists/${id}/`);
    return res.data;
  },

  async create(payload: Partial<Checklist>): Promise<Checklist> {
    const res = await api.post<Checklist>('/api/checklists/checklists/', payload);
    return res.data;
  },

  async update(id: number, payload: Partial<Checklist>): Promise<Checklist> {
    const res = await api.patch<Checklist>(`/api/checklists/checklists/${id}/`, payload);
    return res.data;
  },

  async destroy(id: number): Promise<void> {
    await api.delete(`/api/checklists/checklists/${id}/`);
  },

  async duplicate(id: number): Promise<Checklist> {
    const res = await api.post<Checklist>(`/api/checklists/checklists/${id}/duplicate/`, {});
    return res.data;
  },

  // Checklist runs
  async listRuns(): Promise<ChecklistRunListItem[]> {
    const res = await api.get<ChecklistRunListItem[] | PaginatedResponse<ChecklistRunListItem>>('/api/checklists/checklist-runs/');
    if (Array.isArray(res.data)) {
      return res.data;
    } else if (res.data && 'results' in res.data) {
      return res.data.results;
    }
    return [];
  },

  async retrieveRun(id: number): Promise<ChecklistRun> {
    const res = await api.get<ChecklistRun>(`/api/checklists/checklist-runs/${id}/`);
    return res.data;
  },

  async createRun(payload: CreateChecklistRunRequest): Promise<ChecklistRun> {
    const res = await api.post<ChecklistRun>('/api/checklists/checklist-runs/', payload);
    return res.data;
  },

  async updateRun(id: number, payload: Partial<ChecklistRun>): Promise<ChecklistRun> {
    const res = await api.patch<ChecklistRun>(`/api/checklists/checklist-runs/${id}/`, payload);
    return res.data;
  },

  async completeRun(id: number): Promise<ChecklistRun> {
    const res = await api.post<ChecklistRun>(`/api/checklists/checklist-runs/${id}/complete/`, {});
    return res.data;
  },

  async getRunsByJob(jobId: number): Promise<ChecklistRunListItem[]> {
    const res = await api.get<ChecklistRunListItem[]>(`/api/checklists/checklist-runs/by_job/?job_id=${jobId}`);
    return res.data;
  },

  async getRunsByOpportunity(opportunityId: number): Promise<ChecklistRunListItem[]> {
    const res = await api.get<ChecklistRunListItem[]>(`/api/checklists/checklist-runs/by_opportunity/?opportunity_id=${opportunityId}`);
    return res.data;
  },

  // Step runs
  async listStepRuns(): Promise<ChecklistStepRun[]> {
    const res = await api.get<ChecklistStepRun[] | PaginatedResponse<ChecklistStepRun>>('/api/checklists/checklist-step-runs/');
    if (Array.isArray(res.data)) {
      return res.data;
    } else if (res.data && 'results' in res.data) {
      return res.data.results;
    }
    return [];
  },

  async retrieveStepRun(id: number): Promise<ChecklistStepRun> {
    const res = await api.get<ChecklistStepRun>(`/api/checklists/checklist-step-runs/${id}/`);
    return res.data;
  },

  async updateStepRun(id: number, payload: UpdateChecklistStepRunRequest): Promise<ChecklistStepRun> {
    const res = await api.patch<ChecklistStepRun>(`/api/checklists/checklist-step-runs/${id}/`, payload);
    return res.data;
  },

  async completeStepRun(id: number): Promise<ChecklistStepRun> {
    const res = await api.post<ChecklistStepRun>(`/api/checklists/checklist-step-runs/${id}/complete/`, {});
    return res.data;
  },

  async skipStepRun(id: number, payload: SkipStepRequest): Promise<ChecklistStepRun> {
    const res = await api.post<ChecklistStepRun>(`/api/checklists/checklist-step-runs/${id}/skip/`, payload);
    return res.data;
  },

  async getStepRunsByRun(runId: number): Promise<ChecklistStepRun[]> {
    const res = await api.get<ChecklistStepRun[]>(`/api/checklists/checklist-step-runs/by_run/?run_id=${runId}`);
    return res.data;
  },
};